/*
 * CAPTURE-OWNED MODULE
 * What became of the destination a Publish step points at.
 *
 * A step stores only the destination's id. The destination can since have been
 * deleted or switched off, and the designer has to say so: an id that matches
 * nothing must not look like a step nobody has configured yet.
 *
 * Both the canvas node and the step's panel read the same lists, fetched with
 * captureAPI.destinations.list({ includeInactive: true, includeDeleted: true }):
 *
 *   { destinations: [...active and inactive rows], deleted: [{ id, name }] }
 *
 * `null` means the lists are not known (still loading, or the request failed).
 * NOTHING IS CLAIMED FROM NULL: a failed request must not tell every Publish
 * step that its destination was deleted.
 */

import { createContext } from 'react';

/** The lists above, or null. Provided by the designer for its canvas nodes. */
export const DestinationListsContext = createContext(null);

/** The lists, from a list response; null when the response is not a success. */
export function destinationListsFrom(res) {
  if (!res || res.ok !== true) return null;
  return {
    destinations: Array.isArray(res.destinations) ? res.destinations : [],
    deleted: Array.isArray(res.deleted) ? res.deleted : [],
  };
}

/** The destinations a step may be pointed at: the active ones. */
export function activeDestinations(lists) {
  return lists ? lists.destinations.filter((d) => d.is_active !== false) : [];
}

/**
 * @returns {{ state: 'unset'|'unknown'|'active'|'inactive'|'deleted'|'missing', name: ?string }}
 */
export function destinationStatus(destinationId, lists) {
  if (!destinationId) return { state: 'unset', name: null };
  if (!lists) return { state: 'unknown', name: null };
  const row = lists.destinations.find((d) => d.id === destinationId);
  if (row) return { state: row.is_active === false ? 'inactive' : 'active', name: row.name || null };
  const gone = lists.deleted.find((d) => d.id === destinationId);
  if (gone) return { state: 'deleted', name: gone.name || null };
  return { state: 'missing', name: null };
}

/** The sentence the step's panel shows, or null when there is nothing to say. */
export function destinationProblem(status) {
  switch (status.state) {
    case 'deleted':
      return status.name
        ? `The destination this step used, "${status.name}", was deleted. Choose another destination.`
        : 'The destination this step used was deleted. Choose another destination.';
    case 'missing':
      return 'The destination this step used no longer exists. Choose another destination.';
    case 'inactive':
      return `The destination "${status.name}" is inactive. Reactivate it in Destinations, or choose another destination.`;
    default:
      return null;
  }
}

/** The one line under a Publish node's name on the canvas. */
export function publishNodeSummary(destinationId, lists) {
  const status = destinationStatus(destinationId, lists);
  switch (status.state) {
    case 'unset': return { text: 'Select destination', problem: false };
    case 'deleted': return { text: 'Destination deleted. Choose another.', problem: true };
    case 'missing': return { text: 'Destination no longer exists. Choose another.', problem: true };
    case 'inactive': return { text: 'Destination inactive', problem: true };
    default: return { text: 'Destination configured', problem: false };
  }
}

/**
 * What to tell a person when a request was refused: the server's own sentence
 * when it sent one, otherwise the fallback. Never axios's "Request failed with
 * status code 400", and never a code.
 */
export function serverMessage(err, fallback) {
  const sent = err?.response?.data?.error;
  return typeof sent === 'string' && sent.trim() ? sent : fallback;
}
